import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../../shared/api/client";
import { Card, CardBody, CardHeader, StatusBadge } from "../../shared/ui";
import type { CarryoverView } from "../../shared/types/api";

/**
 * What the sprint did not finish, with the reason for each item.
 *
 * <p>Shown on a closed sprint, beside its metrics. The metrics say the sprint carried three
 * items; this says which three, why each one missed, and where the work went — which is the part
 * a retrospective can actually act on.
 *
 * <p>Absent entirely when the sprint carried nothing. An empty card headed "carried over" reads
 * as a screen that has not loaded rather than as a sprint that finished everything.
 */
export function SprintCarryoverCard({ sprintId }: { sprintId: string }) {
  const carryover = useQuery({
    queryKey: ["sprint-carryover", sprintId],
    queryFn: () => api.get<CarryoverView[]>(`/api/v1/sprints/${sprintId}/carryover`),
  });

  const rows = carryover.data ?? [];
  if (!carryover.isSuccess || rows.length === 0) {
    return null;
  }

  const waiting = rows.filter((row) => !row.movedToSprintId).length;

  return (
    <Card>
      <CardHeader
        title={`Carried over (${rows.length})`}
        subtitle={
          waiting === 0
            ? "All of it has been planned into a later sprint."
            : `${waiting} still waiting in the backlog.`
        }
      />
      <CardBody className="space-y-3">
        {rows.map((row) => (
          <div
            key={row.id}
            className="rounded-lg border border-ink-200 bg-ink-50/40 p-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <p className="min-w-0 text-sm text-ink-900">
                <Link
                  to={`/work-items/${row.itemKey}`}
                  className="font-mono text-xs text-brand-600 hover:underline"
                >
                  {row.itemKey}
                </Link>{" "}
                {row.title}
              </p>
              {row.movedToSprintName ? (
                <StatusBadge tone="blue">Now in {row.movedToSprintName}</StatusBadge>
              ) : (
                <StatusBadge tone="amber">In the backlog</StatusBadge>
              )}
            </div>
            <p className="mt-1 text-xs text-ink-600">
              <span className="font-medium text-ink-800">{row.reasonLabel}</span>
              {row.comment && ` — ${row.comment}`}
            </p>
            {row.recordedByName && (
              <p className="mt-0.5 text-xs text-ink-400">
                Recorded by {row.recordedByName} on{" "}
                {new Date(row.recordedAt).toLocaleDateString()}
              </p>
            )}
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
